import React from "react";
import { css, styled } from "../../../theme/styled/styled/styled";
import { InformationIcon } from "../../icons/new-icons";
import { Body, Title } from "../../typography/Typography";
import { Flex } from "../../util/Flex";
import { VisuallyHidden } from "../../util/VisuallyHidden";

interface Props {
  /* set focus in the field on mount */
  dataTestId?: string;
  /* default true, set to false if there should be no bottom margin */
  gutterBottom?: boolean;
  /* description message */
  helpText?: string;
  /* help text specifically for screen readers. If provided, this will replace helpText when using screen reader. */
  screenReaderHelpText?: string;
  /* icon set to the left of the help text label */
  helpTextIcon?: boolean;
  /* id of the input that this label represents */
  htmlFor?: string;
  /* label text for input field */
  titleText?: string;
  /* text placed to the far right of the label, commonly used to describe whether the input field is required or not */
  endText?: string;
  /* size of the label */
  size?: "small" | "medium";
  /* visually hidden, read by screen readers */
  screenReaderText?: string;
  children?: React.ReactNode;
}

export const FormItemLabel = ({
  children,
  dataTestId,
  helpText,
  helpTextIcon,
  screenReaderHelpText,
  htmlFor,
  titleText,
  endText,
  size = "medium",
}: Props): React.JSX.Element => {
  return (
    <Label
      htmlFor={htmlFor}
      data-test-id={dataTestId}
      data-component-type="FormItemLabel"
    >
      {titleText && (
        <LabelText size={size === "small" ? "small" : "medium"}>
          {titleText}
        </LabelText>
      )}
      <Flex
        justifyContent={helpText ? "space-between" : "flex-end"}
        className={css({ marginBottom: "$spacing-12" })}
      >
        {helpText &&
          (helpTextIcon
            ? (
              <Flex alignItems="center" gap="$spacing-8">
                <InformationIcon size={16} colorToken="$text-secondary" />
                <HelperText
                  aria-hidden={screenReaderHelpText ? "true" : "false"}
                  size="small"
                >
                  {helpText}
                </HelperText>
              </Flex>
            )
            : (
              <HelperText
                aria-hidden={screenReaderHelpText ? "true" : "false"}
                size="small"
              >
                {helpText}
              </HelperText>
            ))}
        {screenReaderHelpText && (
          <VisuallyHidden>
            <p>{screenReaderHelpText}</p>
          </VisuallyHidden>
        )}
        <EndText size="small">{endText}</EndText>
      </Flex>
      {children}
    </Label>
  );
};

const Label = styled.label<{ isDisabled?: boolean }>((p) => ({
  cursor: p.isDisabled ? "default" : "pointer",
  marginBottom: "$spacing-12",
}));

const LabelText = styled(Title)({
  color: "$text-primary",
  fontWeight: 600,
});

const HelperText = styled(Body)<{ children?: string }>(({ children }) => {
  const includesSpecialCharacters = children && /[ÅÄÖ]/.test(children);

  return {
    // Adds extra padding if the text contains characters that takes up extra height
    paddingTop: includesSpecialCharacters ? "$spacing-4" : "$spacing-0",
    color: "$text-secondary",
  };
});

const EndText = styled(Body)({
  color: "$text-secondary",
  fontStyle: "italic",
});
